import React from 'react';
import { Info, CheckCircle, AlertCircle, XCircle, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/**
 * Caja de aviso estándar (info/success/warning/error). Una sola forma: full
 * border + rounded-lg. El dark:border-{c}-500/30 es la razón de ser de este
 * componente: el patrón manual border-{c}-200 sin variante dark dejaba las
 * alertas sin demarcación en modo oscuro.
 */
export type AlertVariant = 'info' | 'success' | 'warning' | 'error';

interface AlertBoxProps {
  variant: AlertVariant;
  title?: string;
  children: React.ReactNode;
  /** Override del icono por defecto de la variante */
  icon?: LucideIcon;
  /** Clases extra para el icono (ej. animate-pulse) */
  iconClassName?: string;
  /** Si se pasa, muestra una X arriba a la derecha */
  onDismiss?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

const VARIANTS: Record<
  AlertVariant,
  { box: string; title: string; body: string; icon: string; Icon: LucideIcon }
> = {
  info: {
    box: 'bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/30',
    title: 'text-blue-800 dark:text-blue-300',
    body: 'text-blue-700 dark:text-blue-300',
    icon: 'text-blue-500',
    Icon: Info,
  },
  success: {
    box: 'bg-green-50 dark:bg-green-500/10 border-green-200 dark:border-green-500/30',
    title: 'text-green-800 dark:text-green-300',
    body: 'text-green-700 dark:text-green-300',
    icon: 'text-green-500',
    Icon: CheckCircle,
  },
  warning: {
    box: 'bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30',
    title: 'text-amber-800 dark:text-amber-300',
    body: 'text-amber-700 dark:text-amber-300',
    icon: 'text-amber-500',
    Icon: AlertCircle,
  },
  error: {
    box: 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/30',
    title: 'text-red-800 dark:text-red-300',
    body: 'text-red-700 dark:text-red-300',
    icon: 'text-red-500',
    Icon: XCircle,
  },
};

const AlertBox: React.FC<AlertBoxProps> = ({
  variant,
  title,
  children,
  icon,
  iconClassName = '',
  onDismiss,
  className = '',
  style,
}) => {
  const v = VARIANTS[variant];
  const IconComp = icon ?? v.Icon;
  return (
    <div className={`relative border rounded-lg p-4 ${v.box} ${className}`} style={style}>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className={`absolute top-2 right-2 p-1 ${v.icon} hover:opacity-75`}
          title="Descartar"
        >
          <X className="h-4 w-4" />
        </button>
      )}
      <div className={`flex ${onDismiss ? 'pr-6' : ''}`}>
        <IconComp className={`h-5 w-5 mt-0.5 mr-3 flex-shrink-0 ${v.icon} ${iconClassName}`} />
        <div className={`text-sm flex-1 ${v.body}`}>
          {title && <p className={`font-medium mb-1 ${v.title}`}>{title}</p>}
          {children}
        </div>
      </div>
    </div>
  );
};

export default AlertBox;
